import { DownloadOutlined, PrinterOutlined } from "@ant-design/icons";
import { Button, Input, Table, Breadcrumb, Space, Select, Tag } from "antd";
import { useEffect, useState } from "react";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import { Link } from "react-router-dom";
import { getShipmentsRequest } from "../../../api/requests/shipment";
import LogoLoader from "../../../components/common/LogoLoader";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { DATE_FORMAT } from "../../../utils/date";
import { api } from "../../../api";
import { useContacts } from "../../../api/hooks/customer";
import { useBols } from "../../../api/hooks/bol";
import { CSVLink } from "react-csv";
import {
  getOrderStatusTagColor,
  getOrderTypeTagColor,
} from "../../../constants";

export async function downloadShipmentPdf({ shipmentId }) {
  const res = await api.get(`/v1/shipment/${shipmentId}/pdf`);
  const pdf_data = res?.data?.data;
  console.log("pdf_data----->", pdf_data);
  const link = document.createElement("a");
  link.target = "_blank";
  link.href = pdf_data?.url;
  link.setAttribute("download", pdf_data?.fileName);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function downloadBolPdf({ bolId }) {
  const res = await api.get(`/v1/bol/${bolId}/pdf`);
  const pdf_data = res?.data?.data;
  console.log("pdf_data----->", pdf_data);
  const link = document.createElement("a");
  link.href = pdf_data?.url;
  link.target = "_blank";
  link.setAttribute("download", pdf_data?.fileName);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function ShipmentsPage() {
  const [downLoadingId, setDownLoadingId] = useState();
  const [bolNumber, setBolNumber] = useState("");
  const [tag, setTag] = useState("");
  const [params, setParams] = useState({});

  const columns = [
    {
      title: "Shipment Id",
      key: "referenceId",
      render: ({ referenceId, _id: shipmentId, bols = [] }) => {
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Button
              onClick={async () => {
                setDownLoadingId(shipmentId);
                await downloadShipmentPdf({ shipmentId });
                // there will be only one bol
                await Promise.all(
                  bols.map(async ({ _id: bolId }) => {
                    return await downloadBolPdf({ bolId });
                  }),
                );
                setDownLoadingId(undefined);
              }}
              type="primary"
              icon={<PrinterOutlined />}
              loading={downLoadingId === shipmentId}
              className="flex-shrink-0"
            ></Button>
            {referenceId}
          </div>
        );
      },
    },
    {
      title: "Order Type",
      dataIndex: "orderType",
      key: "orderType",
      render: (orderType) => {
        return <Tag color={getOrderTypeTagColor(orderType)}>{orderType}</Tag>;
      },
    },
    {
      title: "Shipment Status",
      dataIndex: "shipmentStatus",
      key: "shipmentStatus",
      render: (shipmentStatus) => {
        return (
          <Tag color={getOrderStatusTagColor(shipmentStatus)}>
            {shipmentStatus}
          </Tag>
        );
      },
    },
    {
      title: "Order ID",
      dataIndex: "bols",
      key: "order_ids",
      render: (bols = []) => {
        const orders = bols.map((bol) => {
          return bol?.order;
        });
        // orders.push(bols?.[0]?.order);
        return orders?.map(({ referenceId }) => {
          return (
            <div key={referenceId} className="whitespace-nowrap">
              {referenceId}
            </div>
          );
        });
      },
    },
    {
      title: "Shipment Date",
      dataIndex: "shipmentDate",
      key: "shipmentDate",
      render: (shipmentDate) => dayjs(shipmentDate).format(DATE_FORMAT),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => dayjs(createdAt).format(DATE_FORMAT),
    },
    {
      title: "Carrier Name",
      dataIndex: ["carrier", "name"],
      key: "carrier.name",
      render: (text) => text ?? "-",
    },
    {
      title: "Ordered By",
      dataIndex: ["createdBy", "name"],
      key: "createdBy.name",
      render: (text) => text || "-",
    },
    {
      title: "Driver Name",
      dataIndex: ["driver", "name"],
      key: "driver.name",
      render: (text) => text || "-",
    },
    // {
    //   title: "Orders Count",
    //   key: "orders_count",
    //   render: (data) => {
    //     return data?.bols?.[0]?.items?.length;
    //   },
    // },
    {
      title: "Total Quantity",
      key: "total_quantity",
      render: (data) => {
        return data?.bols?.[0]?.items?.reduce((acc, it) => {
          return acc + (it?.quantity || 0);
        }, 0);
      },
    },
    {
      title: "Unassigned arrivals",
      dataIndex: "unassignedArrivalsCount",
      key: "unassignedArrivalsCount",
      render: (text) => text ?? 0,
    },
  ];

  const { data: carrierList, isLoading: isLoadingCarrierList } = useContacts({
    params: {
      type: "CARRIER",
    },
  });
  const { data: bolListRes, isLoading: isLoadingBolList } = useBols({
    params: {},
  });

  const { data: shipmentListRes, isLoading: isLoadingShipmentList } = useQuery({
    queryKey: ["shipment", params],
    queryFn: async () => {
      const res = await getShipmentsRequest({
        params,
      });
      return res.data;
    },
  });

  function renderTable() {
    if (isLoadingShipmentList) {
      return <LogoLoader />;
    }

    const expandedRowRender = (shipment) => {
      const bolColumns = [
        {
          title: "BoL ID",
          key: "bol_id",
          render: ({ referenceId, _id: bolId }) => {
            return (
              <div className="flex items-center gap-2">
                <Button
                  onClick={async () => {
                    setDownLoadingId(bolId);
                    await downloadBolPdf({ bolId });
                    setDownLoadingId(undefined);
                  }}
                  type="primary"
                  icon={<PrinterOutlined />}
                  loading={downLoadingId === bolId}
                  className="flex-shrink-0"
                ></Button>
                {referenceId}
              </div>
            );
          },
        },
        {
          title: "Order ID",
          dataIndex: "order",
          key: "order_id",
          render: (order) => {
            const { referenceId, _id } = order;
            return (
              <Link
                to={`/processes/order/update/${_id}`}
                className="text-blue-600"
              >
                {referenceId}
              </Link>
            );
          },
        },
        {
          title: "PO Number",
          dataIndex: ["order", "poNumber"],
          key: "po_number",
          render: (text) => text || "-",
        },
        // {
        //   title: "Ordered By",
        //   key: "ordered_by",
        //   render: () => shipment?.createdBy?.name || "-",
        // },
        {
          title: "Shipping Quantity",
          dataIndex: "items",
          key: "shipped_quantity",
          render: (items) =>
            items?.reduce((acc, itm) => {
              return acc + (itm?.quantity || 0);
            }, 0),
        },
        {
          title: "Delivery Address",
          key: "delivery_address",
          render: ({ order }) => {
            const { street, city, state, zipCode, country } =
              order?.customer?.address;
            return `${street}, ${city}`;
            // return `${street} ${city} ${state} ${zipCode} ${country}`;
          },
        },
        {
          title: "Tags",
          render: (details) => {
            const { _id: bolId, tags = [], referenceId, order = {} } = details;
            return (
              <Space>
                <CSVLink
                  filename={`${referenceId}_bol_tags.csv`}
                  data={[
                    ["BoL ID", "Order ID", "PO Number"],
                    [referenceId, order?.referenceId, order?.poNumber],
                    ["Tags"],
                    ...tags?.map((tag) => [tag]),
                  ]}
                >
                  <Button icon={<DownloadOutlined />} type="primary"></Button>
                </CSVLink>
              </Space>
            );
          },
          key: "action",
        },
      ];

      return (
        <Table
          columns={bolColumns}
          dataSource={shipment.bols}
          rowKey="_id"
          pagination={false}
          className="pr-10"
          size="small"
        />
      );
    };

    return (
      <Table
        className="px-6"
        dataSource={shipmentListRes?.data || []}
        columns={columns}
        rowKey={"_id"}
        expandable={{
          expandedRowRender,
        }}
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: shipmentListRes?.count || 0,
          onChange: (page, pageSize) => {
            setParams((prev) => ({
              ...prev,
              page,
              limit: pageSize,
            }));
          },
        }}
        scroll={{
          x: true,
        }}
      />
    );
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setParams((prev) => ({ ...prev, bolNumber: bolNumber }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [bolNumber]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setParams((prev) => ({ ...prev, tag: tag }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [tag]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-6 pb-6">
        <div className="flex flex-col gap-6 pb-6">
          <div className="flex min-h-14 justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
            <Breadcrumb
              items={[
                {
                  title: <div className="text-lg">Shipments</div>,
                },
              ]}
            />
          </div>

          <div className="flex gap-3 px-6">
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Carrier"
              allowClear
              loading={isLoadingCarrierList}
              options={carrierList?.map(({ name, _id }) => ({
                label: name,
                value: _id,
              }))}
              value={params?.carrier}
              onChange={(carrier) => {
                setParams((prev) => ({ ...prev, carrier }));
              }}
              className="min-w-40"
            />
            {/* <Select
              placeholder="Bol"
              allowClear
              loading={isLoadingBolList}
              options={bolListRes?.data?.map(({ referenceId, _id }) => ({
                label: referenceId,
                value: _id,
              }))}
              value={params?.bol}
              onChange={(bol) => {
                setParams((prev) => ({ ...prev, bol }));
              }}
              className="min-w-40"
            /> */}
            <Input
              value={bolNumber}
              onChange={(e) => {
                setBolNumber(e.target.value);
              }}
              className="w-40 min-w-40"
              placeholder="Search BOL Number"
            ></Input>
            <Input
              value={tag}
              onChange={(e) => {
                setTag(e.target.value);
              }}
              className="w-40 min-w-40"
              placeholder="Search by Tag EPC"
            ></Input>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Order Type"
              allowClear
              options={[
                {
                  label: "OUTBOUND",
                  value: "OUTBOUND",
                },
                {
                  label: "INBOUND",
                  value: "INBOUND",
                },
              ]}
              value={params?.orderType}
              onChange={(orderType) => {
                setParams((prev) => ({ ...prev, orderType }));
              }}
              className="min-w-40"
            />
          </div>

          {renderTable()}
        </div>
      </div>
    </div>
  );
}

export default ShipmentsPage;
